/**
 * RENTAL DATE PICKER
 * -------------------
 * Adds the dress to the Shopify cart via /cart/add.js with line item
 * properties: "Start Date (Event)", "Return By", "Hire Method".
 *
 * Hire methods: "Pickup" (self-serve, calendar + add to cart) or
 * "Express" (Express Post). Both methods now use the SAME calendar +
 * Add to Cart flow — Express just records "Express" as the Hire Method
 * property and shows an informational DM panel above the calendar.
 *
 * BOOKED DATES -----------------------------------------------------
 * Reads a JSON blob from a <script data-rental-booked-dates> tag
 * (populated in the Liquid template from the product metafield
 * custom.booked_dates). Format:
 *   [
 *     { "variant_id": 123, "start": "2026-08-01", "end": "2026-08-05" },
 *     { "start": "2026-08-15", "end": "2026-08-19" }
 *   ]
 * "variant_id" is optional — omit it to block that range for every size.
 * Only the exact dates listed (start through end, inclusive) are shown
 * as "Already booked" — this does NOT also block surrounding dates
 * whose computed return period would overlap. Make sure the range you
 * enter covers the dress's full out-of-service period (pickup through
 * return) so the calendar stays accurate.
 * ---------------------------------------------------------------------
 *
 * WIRE-UP REQUIRED (one line) ----------------------------------------
 * Your theme's variant picker (size selector) needs to tell this widget
 * which variant + size label is currently selected. Do that by calling:
 *
 *     window.RentalPicker.setVariant(variantId, sizeLabel, inventoryQty);
 *
 * from your theme's existing "variant changed" handler. If you don't
 * have one wired up yet, this file falls back to auto-detecting the
 * first <select name="id"> or <input name="id"> radio group on the
 * page, which covers most Dawn-based themes out of the box.
 * ---------------------------------------------------------------------
 */

(function () {
  const MIN_LEAD_DAYS = 0;
  const DAY_MS = 24 * 60 * 60 * 1000;

  document.querySelectorAll('.rental-picker').forEach(initPicker);

  function initPicker(root) {
    const state = {
      hireMethod: 'Pickup', // 'Pickup' | 'Express'
      eventDate: null,
      viewMonth: new Date().getMonth(),
      viewYear: new Date().getFullYear(),
      variantId: null,
      sizeLabel: null,
      inventoryQty: null,
      bookedRanges: [], // [{ variantId: string|null, start: Date, end: Date }]
    };

    const els = {
      stockBadge: root.querySelector('[data-rental-stock-badge]'),
      toggleBtns: root.querySelectorAll('[data-hire-method]'),
      expressPanel: root.querySelector('[data-express-panel]'),
      expressNote: root.querySelector('[data-express-note]'),
      calendar: root.querySelector('[data-rental-calendar]'),
      monthLabel: root.querySelector('[data-cal-month-label]'),
      grid: root.querySelector('[data-cal-grid]'),
      prev: root.querySelector('[data-cal-prev]'),
      next: root.querySelector('[data-cal-next]'),
      summarySize: root.querySelector('[data-summary-size]'),
      summaryEvent: root.querySelector('[data-summary-event]'),
      summaryReturn: root.querySelector('[data-summary-return]'),
      summaryMethod: root.querySelector('[data-summary-method]'),
      addBtn: root.querySelector('[data-rental-add-to-cart]'),
      error: root.querySelector('[data-rental-error]'),
      summaryHeading: root.querySelector('[data-rental-summary-heading]'),
      summaryBlock: root.querySelector('[data-rental-summary]'),
    };

    // ---- public API for the theme's variant picker to call ----------
    window.RentalPicker = window.RentalPicker || {};
    window.RentalPicker.setVariant = function (variantId, sizeLabel, inventoryQty) {
      state.variantId = variantId;
      state.sizeLabel = sizeLabel || null;
      state.inventoryQty = typeof inventoryQty === 'number' ? inventoryQty : null;
      els.summarySize.textContent = sizeLabel || '—';
      renderStockBadge();

      // A different size can have different booked ranges (or none), and
      // the previously selected date may no longer be valid, so re-check.
      if (state.eventDate && isDateBooked(state.eventDate)) {
        state.eventDate = null;
        updateSummary();
      }
      renderCalendar();
      updateAddButtonState();
    };

    state.bookedRanges = loadBookedRanges();
    initVariantDetection();

    // ---- toggle: Pickup / Express ------------------------------------
    els.toggleBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        els.toggleBtns.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        state.hireMethod = btn.dataset.hireMethod; // 'Pickup' | 'Express'
        els.summaryMethod.textContent =
          state.hireMethod === 'Express' ? 'Express Post' : 'Pickup (Russell Lea)';
        toggleExpressUI();
        hideError();
        updateAddButtonState();
      });
    });

    // Both Pickup and Express now share the exact same calendar +
    // summary + Add to Cart flow. Express just also shows the DM
    // info panel/note above the calendar for context — it no longer
    // hides the calendar, summary, or Add to Cart button.
    function toggleExpressUI() {
      const isExpress = state.hireMethod === 'Express';

      if (els.expressPanel) els.expressPanel.hidden = !isExpress;
      if (els.expressNote) els.expressNote.hidden = !isExpress;

      // Calendar always visible
      if (els.calendar) els.calendar.hidden = false;

      // Booking summary always visible
      if (els.summaryHeading) els.summaryHeading.hidden = false;
      if (els.summaryBlock) els.summaryBlock.hidden = false;

      // Add to Cart button always visible/enabled-by-state for both methods
      if (els.addBtn) els.addBtn.hidden = false;

      updateAddButtonState();
    }

    // ---- calendar nav ---------------------------------------------------
    els.prev.addEventListener('click', () => {
      state.viewMonth -= 1;
      if (state.viewMonth < 0) {
        state.viewMonth = 11;
        state.viewYear -= 1;
      }
      renderCalendar();
    });
    els.next.addEventListener('click', () => {
      state.viewMonth += 1;
      if (state.viewMonth > 11) {
        state.viewMonth = 0;
        state.viewYear += 1;
      }
      renderCalendar();
    });

    // ---- add to cart ----------------------------------------------------
    els.addBtn.addEventListener('click', addToCart);

    renderCalendar();
    toggleExpressUI();

    // =====================================================================

    function loadBookedRanges() {
      const el = document.querySelector('[data-rental-booked-dates]');
      if (!el) return [];
      let raw;
      try {
        raw = JSON.parse(el.textContent || '[]');
      } catch (e) {
        console.warn('[RentalPicker] Could not parse booked dates JSON', e);
        return [];
      }
      if (!Array.isArray(raw)) return [];

      return raw
        .map((r) => {
          const start = stripTime(new Date(r.start));
          const end = stripTime(new Date(r.end));
          if (isNaN(start) || isNaN(end)) return null;
          return {
            variantId: r.variant_id ? String(r.variant_id) : null,
            start,
            end,
          };
        })
        .filter(Boolean);
    }

    // Is this specific calendar date inside a booked range that applies
    // to the currently selected variant (a range with no variantId
    // applies to every variant)? Only the exact dates entered in the
    // metafield JSON are treated as booked — this does NOT also block
    // surrounding dates whose computed return period would overlap.
    function isDateBooked(date) {
      return state.bookedRanges.some((r) => {
        if (r.variantId && state.variantId && r.variantId !== String(state.variantId)) {
          return false;
        }
        return date >= r.start && date <= r.end;
      });
    }

    function initVariantDetection() {
      const jsonEl = document.querySelector('[data-rental-product-json]');
      let productData = null;
      try {
        productData = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      } catch (e) {
        console.warn('[RentalPicker] Could not parse embedded product JSON', e);
      }

      function variantById(id) {
        if (!productData) return null;
        return productData.variants.find((v) => String(v.id) === String(id)) || null;
      }

      function applyById(id) {
        const v = variantById(id);
        if (v) {
          window.RentalPicker.setVariant(
            v.id,
            v.title,
            v.inventory_management ? v.inventory_quantity : null
          );
        } else {
          window.RentalPicker.setVariant(id, null, null);
        }
      }

      const defaultId = root.dataset.defaultVariantId;
      if (defaultId) applyById(defaultId);

      const watchNativeInputs = () => {
        const select = document.querySelector('select[name="id"]');
        const radios = document.querySelectorAll('input[name="id"]');
        if (select) {
          select.addEventListener('change', () => applyById(select.value));
        }
        radios.forEach((r) =>
          r.addEventListener('change', () => {
            if (r.checked) applyById(r.value);
          })
        );
      };
      watchNativeInputs();

      ['variant:change', 'variant:changed', 'product:variant-change', 'productVariantChange'].forEach(
        (evtName) => {
          document.addEventListener(evtName, (e) => {
            const id = e.detail?.variant?.id || e.detail?.id;
            if (id) applyById(id);
          });
        }
      );

      let lastSeenValue = defaultId || null;
      setInterval(() => {
        const el = document.querySelector(
          'select[name="id"], input[name="id"]:checked, input[name="id"][type="hidden"]'
        );
        if (el && el.value && el.value !== lastSeenValue) {
          lastSeenValue = el.value;
          applyById(el.value);
        }
        const urlVariant = new URLSearchParams(window.location.search).get('variant');
        if (urlVariant && urlVariant !== lastSeenValue) {
          lastSeenValue = urlVariant;
          applyById(urlVariant);
        }
      }, 400);
    }

    function renderStockBadge() {
      if (state.inventoryQty === null) {
        els.stockBadge.hidden = true;
        return;
      }
      els.stockBadge.hidden = false;
      if (state.inventoryQty <= 0) {
        els.stockBadge.textContent = 'SOLD OUT';
      } else if (state.inventoryQty <= 2) {
        els.stockBadge.textContent = `🔥 ONLY ${state.inventoryQty} LEFT`;
      } else {
        els.stockBadge.textContent = 'AVAILABLE';
      }
    }

    function renderCalendar() {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ];
      els.monthLabel.textContent = `${monthNames[state.viewMonth]} ${state.viewYear}`;

      const firstOfMonth = new Date(state.viewYear, state.viewMonth, 1);
      const startOffset = (firstOfMonth.getDay() + 6) % 7;
      const daysInMonth = new Date(state.viewYear, state.viewMonth + 1, 0).getDate();

      const today = stripTime(new Date());
      const minSelectable = new Date(today.getTime() + MIN_LEAD_DAYS * DAY_MS);

      els.grid.innerHTML = '';

      for (let i = 0; i < startOffset; i++) {
        els.grid.appendChild(cell('', 'is-empty'));
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(state.viewYear, state.viewMonth, day);
        const isPast = date < minSelectable;
        const isBooked = !isPast && isDateBooked(date);
        const isReturnDay = state.eventDate && sameDay(date, computeReturnDate(state.eventDate));
        const isEventDay = state.eventDate && sameDay(date, state.eventDate);

        let stateClass = 'is-selectable';
        if (isPast) stateClass = 'is-disabled';
        else if (isBooked) stateClass = 'is-booked';

        const btn = cell(String(day), stateClass);
        if (isEventDay) btn.classList.add('is-selected');
        if (isReturnDay && !isEventDay) btn.classList.add('is-return-day');

        if (isBooked) {
          btn.disabled = true;
          btn.title = 'Already booked for this period';
        }

        if (!isPast && !isBooked) {
          btn.addEventListener('click', () => selectDate(date));
        }
        els.grid.appendChild(btn);
      }
    }

    function cell(text, extraClass) {
      const div = document.createElement('button');
      div.type = 'button';
      div.className = `rental-picker__cal-day ${extraClass}`;
      div.textContent = text;
      if (extraClass === 'is-empty') div.disabled = true;
      return div;
    }

    function selectDate(date) {
      state.eventDate = stripTime(date);
      renderCalendar();
      updateSummary();
      updateAddButtonState();
    }

    function computeReturnDate(eventDate) {
      const d = stripTime(eventDate);
      const day = d.getDay();
      let daysUntilNextMonday = (8 - day) % 7;
      if (daysUntilNextMonday === 0) daysUntilNextMonday = 7;
      return new Date(d.getTime() + daysUntilNextMonday * DAY_MS);
    }

    function updateSummary() {
      if (!state.eventDate) {
        els.summaryEvent.textContent = '—';
        els.summaryReturn.textContent = '—';
        return;
      }
      els.summaryEvent.textContent = formatDate(state.eventDate);
      const returnDate = computeReturnDate(state.eventDate);
      els.summaryReturn.textContent = `${formatDate(returnDate)} by 12:00pm`;
    }

    // Same logic for Pickup AND Express — both need a variant, a date,
    // in-stock inventory, and an unbooked date before Add to Cart works.
    function updateAddButtonState() {
      const hasDate = !!state.eventDate;
      const hasVariant = !!state.variantId;
      const inStock = state.inventoryQty === null || state.inventoryQty > 0;
      const dateIsBooked = hasDate && isDateBooked(state.eventDate);

      els.addBtn.disabled = !(hasDate && hasVariant && inStock && !dateIsBooked);
      els.addBtn.textContent = !hasVariant
        ? 'Select a size'
        : !inStock
        ? 'Sold out'
        : dateIsBooked
        ? 'Date unavailable'
        : !hasDate
        ? 'Select a date'
        : 'Book Your Hire';
    }

    async function addToCart() {
      hideError();
      if (!state.eventDate || !state.variantId) return;

      const returnDate = computeReturnDate(state.eventDate);

      if (isDateBooked(state.eventDate)) {
        showError('Sorry, that date is already booked for this size. Please choose another date.');
        renderCalendar();
        updateAddButtonState();
        return;
      }

      const properties = {
        'Start Date (Event)': formatDate(state.eventDate),
        'Return By': `${formatDate(returnDate)} by 12:00pm`,
        'Hire Method': state.hireMethod,
        // Hidden (Shopify auto-hides "_"-prefixed properties from the
        // customer/admin order view) — ISO dates for Shopify Flow to
        // read when auto-updating the booked_dates metafield.
        '_start_iso': formatIso(state.eventDate),
        '_return_iso': formatIso(returnDate),
      };

      els.addBtn.disabled = true;
      els.addBtn.textContent = 'Adding…';

      const sectionIds = Array.from(
        new Set([...discoverCartSectionIds(), 'cart-drawer', 'cart-icon-bubble'])
      );

      try {
        let res = await postCartAdd(sectionIds, properties);
        let data = await res.json();

        if (!res.ok) {
          res = await postCartAdd([], properties);
          data = await res.json();
          if (!res.ok) {
            throw new Error(data.description || data.message || 'Could not add this item to your cart.');
          }
        }

        root.dispatchEvent(new CustomEvent('rental:added-to-cart', { bubbles: true, detail: data }));

        const patchedSections = applyDawnCartSections(data.sections);
        openDawnCartDrawer();

        if (!patchedSections) {
          await refreshCartUIAndOpenDrawer();
        }

        els.addBtn.textContent = 'Added ✓';
        setTimeout(updateAddButtonState, 1500);
      } catch (err) {
        showError(err.message || 'Something went wrong adding this to your cart.');
        updateAddButtonState();
      }
    }

    function postCartAdd(withSectionIds, itemProperties) {
      return fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: state.variantId,
              quantity: 1,
              properties: itemProperties,
            },
          ],
          sections: withSectionIds.length ? withSectionIds : undefined,
          sections_url: window.location.pathname,
        }),
      });
    }

    function applyDawnCartSections(sections) {
      if (!sections) return false;
      let patchedAny = false;

      if (sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const freshInner = doc.querySelector('#CartDrawer .drawer__inner') || doc.querySelector('.drawer__inner');
        const liveInner = document.querySelector('#CartDrawer .drawer__inner');
        if (freshInner && liveInner) {
          liveInner.innerHTML = freshInner.innerHTML;
          patchedAny = true;
        }
        const wrapper = document.getElementById('shopify-section-cart-drawer');
        if (wrapper) {
          wrapper.innerHTML = sections['cart-drawer'];
          patchedAny = true;
        }
      }

      if (sections['cart-icon-bubble']) {
        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const freshBubble = doc.getElementById('cart-icon-bubble');
        const liveBubble = document.getElementById('cart-icon-bubble');
        if (freshBubble && liveBubble) {
          liveBubble.innerHTML = freshBubble.innerHTML;
          patchedAny = true;
        }
      }

      const drawerEl = document.querySelector('cart-drawer');
      if (drawerEl && patchedAny) {
        drawerEl.classList.remove('is-empty');
      }

      return patchedAny;
    }

    function openDawnCartDrawer() {
      const drawerEl = document.querySelector('cart-drawer');
      if (!drawerEl) return false;
      if (typeof drawerEl.open === 'function') {
        drawerEl.open();
      } else {
        drawerEl.classList.add('active');
      }
      return true;
    }

    function discoverCartSectionIds() {
      const ids = new Set();

      document.querySelectorAll('[id^="shopify-section-"]').forEach((el) => {
        if (/cart/i.test(el.id)) {
          ids.add(el.id.replace(/^shopify-section-/, ''));
        }
      });

      const cartLikeSelectors = [
        'cart-drawer', '#CartDrawer', '.cart-drawer', '[id*="cart-drawer" i]',
        '[class*="cart-drawer" i]', '[id*="minicart" i]', '[class*="minicart" i]',
        '[id*="mini-cart" i]', '[class*="mini-cart" i]', '[data-cart-drawer]', '[data-drawer="cart"]',
      ];
      cartLikeSelectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          const sectionEl = el.closest('[id^="shopify-section-"]');
          if (sectionEl) ids.add(sectionEl.id.replace(/^shopify-section-/, ''));
        });
      });

      return Array.from(ids);
    }

    function applySectionHtml(sections) {
      if (!sections) return false;
      let patchedAny = false;
      Object.entries(sections).forEach(([id, html]) => {
        const wrapper = document.getElementById(`shopify-section-${id}`);
        if (wrapper && html) {
          wrapper.innerHTML = html;
          patchedAny = true;
        }
      });
      return patchedAny;
    }

    async function refreshCartUIAndOpenDrawer() {
      let cart = null;
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        cart = await res.json();
      } catch (e) {
        console.warn('[RentalPicker] Could not fetch /cart.js for UI refresh', e);
      }

      if (cart) {
        document
          .querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble, #CartCount, .js-cart-count')
          .forEach((el) => {
            el.textContent = cart.item_count;
          });
      }

      [
        'cart:refresh', 'cart:updated', 'cart:update', 'cart:build', 'cart:change',
        'CartDrawer:refresh', 'cart-drawer:refresh', 'ajaxProduct:added', 'product:added',
      ].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { cart } }));
      });

      const cartLink = document.querySelector(
        '#cart-icon-bubble, [data-cart-icon], a[href="/cart"], .header__icon--cart, .site-header__cart-link, #CartLink, .icon-cart, [href*="/cart"]'
      );
      let clickedRealIcon = false;
      if (cartLink) {
        cartLink.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        clickedRealIcon = true;
      }

      if (!clickedRealIcon) {
        const drawerSelectors = [
          'cart-drawer', '#CartDrawer', '.cart-drawer', '#sidebar-cart',
          '.drawer--cart', '#CartDrawerContainer', '[data-cart-drawer]',
        ];
        for (const sel of drawerSelectors) {
          const el = document.querySelector(sel);
          if (!el) continue;
          if (typeof el.open === 'function') {
            el.open();
          } else {
            el.classList.add('active', 'is-open', 'open', 'drawer--open');
            el.removeAttribute('hidden');
            el.setAttribute('aria-hidden', 'false');
          }
        }
      }
    }

    function showError(msg) {
      els.error.hidden = false;
      els.error.textContent = msg;
    }
    function hideError() {
      els.error.hidden = true;
      els.error.textContent = '';
    }
    function stripTime(d) {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    function sameDay(a, b) {
      return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    }
    function formatDate(d) {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${dd}-${mm}-${d.getFullYear()}`;
    }
    function formatIso(d) {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${d.getFullYear()}-${mm}-${dd}`;
    }
  }
})();